import React, { useMemo } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { TEXT_STYLES } from "../ui/CamberTheme";
export function Pagination({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  variant = "full", // "full" | "compact"
  className = "",
}) {
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const pages = useMemo(() => {
    const res = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) res.push(i);
      return res;
    }

    if (currentPage <= 3) return [1, 2, 3, 4, "...", totalPages];
    if (currentPage >= totalPages - 2)
      return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];

    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  }, [currentPage, totalPages]);

  const goTo = (p) => {
    if (typeof p !== "number") return;
    if (p < 1 || p > totalPages) return;
    if (p === currentPage) return;
    onPageChange(p);
  };

  const NavButton = ({ dir, disabled, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "w-[32px] h-[32px] bg-camber-surface border border-camber-border flex items-center justify-center",
        "text-camber-textMuted disabled:opacity-40 disabled:cursor-not-allowed",
        dir === "left" ? "rounded-l-[4px]" : "rounded-r-[4px]",
        "hover:bg-camber-hover hover:border-camber-borderHover",
      ].join(" ")}
      aria-label={dir === "left" ? "Previous page" : "Next page"}
    >
      {dir === "left" ? <ChevronLeftIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
    </button>
  );

  // --- Compact variant (matches Videos top-right) ---
  if (variant === "compact") {
    return (
      <div className={`flex items-center gap-[8px] ${className}`}>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className={[
            "h-[32px] bg-camber-surface border border-camber-border rounded-[4px] px-[8px]",
            "text-[12px] leading-[155%] text-camber-text",
            "focus:outline-none focus:ring-2 focus:ring-camber-borderHover focus:border-camber-borderHover",
          ].join(" ")}
        >
          <option value={5}>5 per page</option>
          <option value={10}>10 per page</option>
          <option value={20}>20 per page</option>
          <option value={50}>50 per page</option>
        </select>

        <div className="flex items-center">
          <NavButton
            dir="left"
            disabled={currentPage === 1}
            onClick={() => goTo(currentPage - 1)}
          />

          {/* active page tile */}
          <div
            className={[
              "w-[32px] h-[32px] flex items-center justify-center border-y border-camber-border",
              "bg-camber-primary text-white text-[12px] leading-[155%]",
            ].join(" ")}
            aria-label={`Page ${currentPage}`}
          >
            {currentPage}
          </div>

          <NavButton
            dir="right"
            disabled={currentPage === totalPages}
            onClick={() => goTo(currentPage + 1)}
          />
        </div>
      </div>
    );
  }

  // --- Full variant (footer style) ---
  return (
    <div
      className={[
        "flex items-center justify-between border-t border-camber-border bg-camber-surface px-4 py-3",
        className,
      ].join(" ")}
    >
      <div className="flex items-center gap-4">
        <p className={`text-sm ${TEXT_STYLES.muted}`}>
          Showing <span className="font-medium text-camber-text">{startItem}</span> to{" "}
          <span className="font-medium text-camber-text">{endItem}</span> of{" "}
          <span className="font-medium text-camber-text">{totalCount}</span> results
        </p>

        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className={[
            "h-[32px] bg-camber-surface border border-camber-border rounded-[4px] px-[8px]",
            "text-[12px] leading-[155%] text-camber-text",
            "focus:outline-none focus:ring-2 focus:ring-camber-borderHover focus:border-camber-borderHover",
          ].join(" ")}
        >
          <option value={5}>5 per page</option>
          <option value={10}>10 per page</option>
          <option value={20}>20 per page</option>
          <option value={50}>50 per page</option>
        </select>
      </div>

      <nav className="isolate inline-flex -space-x-px rounded-[4px]" aria-label="Pagination">
        <button
          type="button"
          onClick={() => goTo(currentPage - 1)}
          disabled={currentPage === 1}
          className={[
            "relative inline-flex items-center rounded-l-[4px] px-2 py-2",
            "bg-camber-surface border border-camber-border text-camber-textMuted",
            "hover:bg-camber-hover hover:border-camber-borderHover",
            "disabled:opacity-40 disabled:cursor-not-allowed",
          ].join(" ")}
        >
          <span className="sr-only">Previous</span>
          <ChevronLeftIcon className="h-5 w-5" />
        </button>

        {pages.map((p, idx) =>
          p === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-camber-textMuted border-y border-camber-border"
            >
              ...
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => goTo(p)}
              className={[
                "relative inline-flex items-center px-4 py-2 text-sm font-semibold",
                "border-y border-camber-border",
                "hover:bg-camber-hover hover:border-camber-borderHover",
                p === currentPage
                  ? "bg-camber-primary text-white border-camber-primary"
                  : "bg-camber-surface text-camber-text",
              ].join(" ")}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => goTo(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={[
            "relative inline-flex items-center rounded-r-[4px] px-2 py-2",
            "bg-camber-surface border border-camber-border text-camber-textMuted",
            "hover:bg-camber-hover hover:border-camber-borderHover",
            "disabled:opacity-40 disabled:cursor-not-allowed",
          ].join(" ")}
        >
          <span className="sr-only">Next</span>
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </nav>
    </div>
  );
}
